import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { startIndeterminate, stopIndeterminate } from "./lib/routeProgress";
import { reportProgress, resetProgress } from "./lib/uploadProgress";
import { initPush } from "./lib/push";
import { kaynagiOku } from "./lib/kaynak";
import { getCountry } from "./country";
import { apiBase, oturumUlkesiniDamgala, oturumUlkesiniDenetle } from "./apiBase";

// TEK-5 (29 Agu 2026): backend adresi artik DERLEME ZAMANI sabiti degil.
// Tek uygulama iki ulkeye hizmet ediyor; adres `apiBase()` ile CAGRI
// ANINDA, secili ulkeye gore hesaplaniyor. Modul duzeyinde sabitlenirse
// ulke degisimi yansimaz — bkz. apiBase.js.

const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  loading: true,
});

export function AuthProvider({ children }) {
  // TEK-5 (29 Agu 2026): jeton BIR BACKEND'E aittir. Turkiye'de alinan jeton
  // StrayPaw sunucusunda gecersizdir. Ulke degismisse jetonu okumadan ONCE
  // temizliyoruz; yoksa `/auth/me` 401 doner ve kullanici nedenini bilmeden
  // cikis yapmis gorunur. Damgasi olmayan eski jetonlara dokunulmuyor.
  const [token, setToken] = useState(() => {
    oturumUlkesiniDenetle();
    return localStorage.getItem("token");
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${apiBase()}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        // Only log the user out on a genuine auth rejection (token invalid or
        // expired). A network blip or a backend hiccup (5xx / timeout) must NOT
        // wipe the token — otherwise a transient error silently signs people out
        // and bounces them to /login on the next protected action (e.g. Kedi ekle).
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          setToken(null);
          localStorage.removeItem("token");
        }
        // else: keep the token; user stays null this load but a refresh recovers.
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [token]);

  // Register this device for push notifications once the user is signed in.
  // Silent no-op on web or until the native @capacitor/push-notifications
  // plugin is installed — never blocks anything.
  useEffect(() => {
    if (user?.id) { initPush(); }
  }, [user?.id]);

  const login = async (email, password) => {
    const res = await axios.post(`${apiBase()}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    oturumUlkesiniDamgala();   // TEK-5: jeton hangi ulkenin backend'ine ait
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const refreshUser = async () => {
    const t = localStorage.getItem("token");
    if (!t) return null;
    try {
      const res = await axios.get(`${apiBase()}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  const register = async (payload) => {
    const res = await axios.post(`${apiBase()}/auth/register`, payload);
    localStorage.setItem("token", res.data.token);
    oturumUlkesiniDamgala();   // TEK-5: jeton hangi ulkenin backend'ine ait
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  // Sign in with Google. Same call for a brand-new user and a returning one —
  // the backend creates the account if it has never seen this address, so there
  // is no "register vs log in" fork to make someone choose between while a hurt
  // cat waits. `idToken` comes from Google, and only Google can mint one.
  const loginWithGoogle = async (idToken) => {
    const res = await axios.post(`${apiBase()}/auth/google`, {
      id_token: idToken,
      // B.3: Google ile gelen kaynagi kaybetmek, verinin buyuk kismini
      // kaybetmek olabilir -- e-posta ile kayit tek yol degil.
      signup_source: kaynagiOku(),
    });
    localStorage.setItem("token", res.data.token);
    oturumUlkesiniDamgala();   // TEK-5: jeton hangi ulkenin backend'ine ait
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  // Apple ile giriş (iOS). Google'ın aynısı: tek çağrı hem kayıt hem giriş.
  // Sunucuya YALNIZCA Apple'ın imzaladığı jeton gider; ad ayrı gelir çünkü
  // Apple onu sadece ilk girişte veriyor.
  const loginWithApple = async ({ identity_token, given_name, family_name }) => {
    const res = await axios.post(`${apiBase()}/auth/apple`, {
      identity_token, given_name, family_name,
      signup_source: kaynagiOku(),   // B.3
    });
    localStorage.setItem("token", res.data.token);
    oturumUlkesiniDamgala();   // TEK-5: jeton hangi ulkenin backend'ine ait
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    localStorage.removeItem("token");
    // Reset one-time onboarding milestones so a new user on the same
    // browser sees them fresh.
    try {
      for (const k of [
        "firstCatMilestone:v1",
        "milestone5Cats:v1",
        "milestone10Cats:v1",
        "milestone25Cats:v1",
      ]) {
        localStorage.removeItem(k);
      }
    } catch { /* noop */ }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, token, login, loginWithGoogle, loginWithApple, register, logout, loading, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const apiClient = () => {
  const token = localStorage.getItem("token");
  const client = axios.create({
    baseURL: apiBase(),
    // 90s ceiling: GPT-Vision matches take 5–20s typically; this keeps the
    // UI from "hanging" forever if the upstream is slow.
    timeout: 90000,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    // Real upload progress — fires for every POST/PUT with a body large
    // enough to matter (photos). reportProgress filters tiny JSON bodies.
    onUploadProgress: (e) => {
      if (!e || !e.total) return;
      reportProgress(e.loaded, e.total);
    },
  });
  // Drive the global top indeterminate progress bar for the duration of
  // every API call. Refcounted, so multiple concurrent requests collapse
  // into a single visible stripe.
  client.interceptors.request.use((config) => {
    startIndeterminate();
    // Ülke başlığı: backend tr+id paylaşımlı olduğu için ülkeyi çözemeyince
    // varsayılan EUR'a düşüyordu (ödül ₺ yerine € çıkıyordu). Cihaz ülkesini
    // her isteğe damgalayıp sunucunun doğru para birimini seçmesini sağlıyoruz.
    try {
      const c = getCountry();
      if (c) config.headers["X-Country"] = c;
    } catch (_) { /* getCountry yoksa sessizce geç */ }
    return config;
  });
  const onDone = (v) => { stopIndeterminate(); return v; };
  const onErr = (e) => {
    stopIndeterminate();
    // If the request failed mid-upload, clear the bottom progress UI too.
    resetProgress();
    return Promise.reject(e);
  };
  client.interceptors.response.use(onDone, onErr);
  return client;
};
